import Vue from 'vue'
import Vuex from 'vuex'
import VuexORM from '@vuex-orm/core'
import database from './database'
import { createNewProject } from './mixins'
import { i18n } from '@/locale/i18n'

Vue.use(Vuex)

const state = {
  // public app menu
  // in case of admin, will add extra bellow
  // actually we're using only the keys now, as the values are translated
  menu: {
    welcome: 'Welcome',
    guide: 'Guide',
    tool: 'Tool'
  },

  // user profile
  /* user: {
    is_guest: true,
    is_admin: false,
    profile: {}
  }, */

  // current element (form & drawing)
  // just change this with another elementID from store, and will be displayed by Vue
  elementId: null,

  // selected item ID (load or opening)
  // used to sync drawing & table
  selectedItem: null,

  // localStorage key (projects & elements)
  // there's an extra key with '_selected' suffix which holds the current viewed element (to restore that one too)
  lsKey: 'CRH-slabs',

  // here will load projects & elements from server of an authenticated user
  apiData: null,

  // GENERAL SWITCH to allow modifing locked or busy elements
  // to be aware of loading initial data (from localStorage or apiData) without warnings if loads or opening present on a calculated element (cases where loads & openings were being silently droped)
  // needed to avoid unlock messages while adding loads & openings already present (block meant for user changes only)
  // it will be made false as soon as default data is loaded
  allowChanges: true,

  // here we store all pages and their loaded locales
  pageLocales: {}
}

export default function buildStore (initialState = {}) {
  // /////////////////////////////////////////////////////
  // fake user & projects --------------
  /* const appConfig = require('./_tmp/appConfig.json')

  appConfig.state.user = {
    first_name: 'Răzvan',
    is_admin: false
  }

  // appConfig.state.user = null

  appConfig.state.apiData = require('./_tmp/fakeProjects.json')

  initialState = {
    ...initialState,
    ...appConfig.state
  } */
  // fake user & projects --------------
  // /////////////////////////////////////////////////////

  // inject admin into menu (if admin)
  if (initialState && initialState.user && initialState.user.is_admin) {
    state.menu.reports = 'Reports'
    state.menu.admin = 'Admin'
  }

  return new Vuex.Store({
    // strict: process.env.NODE_ENV !== 'production',

    state: {
      ...state,
      ...initialState
    },

    getters: {
      userIsGuest: (state) => {
        if (!state.user || state.user === null || !state.user.first_name) {
          return true
        } else {
          // return false (not guest) if local state says is authenticated, but enforce server check as well and update state
          uikit.util.ajax('api/user?customMethod=check', {
            method: 'get',
            responseType: 'json',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
              'X-Requested-With': 'XMLHttpRequest'
            }
          }).then(function (xhr) {
            if (!xhr.response || xhr.response.is_guest) {
              Vue.delete(state.user, 'first_name')
              Vue.set(state.user, 'is_admin', false)
              Vue.delete(state.menu, 'admin')
              Vue.delete(state.menu, 'reports')
            }
          })

          return false
        }
      },

      userIsAdmin: (state) => {
        if (state.user && state.user.is_admin && state.user.is_admin !== '0') {
          // return true if local state says is admin, but enforce server check as well and update state
          uikit.util.ajax('api/user?customMethod=check', {
            method: 'get',
            responseType: 'json',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
              'X-Requested-With': 'XMLHttpRequest'
            }
          }).then(function (xhr) {
            if (!xhr.response || !xhr.response.is_admin) {
              Vue.set(state.user, 'is_admin', false)
              Vue.delete(state.menu, 'admin')
              Vue.delete(state.menu, 'reports')

              // enforce going home (might be on soma admin only page)
              Vue.router.push('/')
            }
          })

          return true
        } else {
          return false
        }
      },

      opcoId: (state) => {
        const companies = state.companies

        // default is first OpCo
        let opcoId = Object.keys(companies)[0]

        // in production mode try to find it based on domain name (not subdomain)
        if (process.env.NODE_ENV === 'production') {
          const domain = /[^.]+(\.[^.]+)?$/.exec(window.location.hostname)

          const byDomain = Object.keys(companies).find(id => companies[id].domain === domain[0])

          if (byDomain) {
            opcoId = byDomain
          } else {
            console.warn('Couldn\'t detect OpCo based on domain name, went with default!')
          }
        }

        return parseInt(opcoId, 10)
      },

      opco: (state, getters) => {
        return state.companies[getters.opcoId]
      },

      opcoEmail: (state, getters) => {
        return getters.opco.contact_email || 'hollowcore@ergon.be'
      },

      opcoPhone: (state, getters) => {
        return getters.opco.contact_phone || '+32 3 490 04 11'
      },

      opcoReverseEmail: (state, getters) => {
        return getters.opcoEmail.split('').reverse().join('')
      },

      // get current slab / element (with loads &openings)
      // empty object fallback as of we are setting elementId in the Promise resolve of the ORM than this will first render with null and raise template errors
      element: (state, getters) => getters['db/slabs/query']()
        .with('loads')
        .with('openings')
        .find(state.elementId) || {},

      // get current slab's project (with contats)
      project: (state, getters) => getters['db/projects/query']()
        .with('contacts')
        .find(getters.element.project_id) || {},

      // get productTypes of specific OpCo (id)
      opcoProductTypes: (state) => (opcoId) => Object.keys(state.product_types).filter(typeId => state.product_types[typeId].opco_id === opcoId).reduce((a, c) => {
        a[c] = state.product_types[c]; return a
      }, {}),

      // productType data (based on floor data: productTypeId, thickness & width)
      productTypeData: (state) => (id, thickness, width) => state.product_types[id].thickness[thickness].filter(product => product.width === width)[0],

      // return ALL unsaved data (projects & slabs), with all details needed, prepared for send / sync to server (remove empty contacts)
      unsavedData: (state, getters) => {
        return {
          projects: getters['db/projects/query']()
            .where('savedOnServer', false)
            .with('contacts')
            .get()
            .map(project => project.prepareSend), // remove blank contacts,

          slabs: getters['db/slabs/query']()
            .where('savedOnServer', false)
            .with('loads')
            .with('openings')
            .get()
            .map(slab => {
              if (slab.calculation_pdf && slab.calculation_pdf !== 'null' && slab.calculation_pdf.length) {
                slab.calculation_pdf = 1
              }

              return slab
            }) // if calculation_pdf present, just send 1 instead of a very long string for each element (although no element with calculated PDF should be unsaved, but let's be sure)
        }
      },

      // simple check if there's something unsaved
      isEverythingSaved: (state, getters) => Object.keys(getters.unsavedData).reduce((a, c) => a + getters.unsavedData[c].length, 0) === 0
    },

    mutations: {
      // set current working element id
      setElementId (state, elementId) {
        state.elementId = elementId

        // save it to localStorage as well
        try {
          localStorage.setItem(`${state.lsKey}_selected`, elementId)
        } catch (err) {
        }
      },

      // set selectedItem ID
      setSelectedItem (state, id) {
        state.selectedItem = id
      },

      // this might be a bad practice if already overused for model non-fields properties (such as savedOnServer, etc)
      // just because it is a general switch (ALL elements) and might interfere with non-wanted elements ...
      allowChanges (state, allow) {
        state.allowChanges = allow
      },

      setPageLocaleMessages (state, { page, locale, messages }) {
        if (messages) {
          if (!state.pageLocales[page]) {
            state.pageLocales[page] = {
              locale: messages
            }
          } else {
            state.pageLocales[page][locale] = messages
          }
        } else {
          messages = state.pageLocales[page][locale]
        }

        // I don't know why this is not triggering reactivity (although they say it has been fixed for merge functions in older v7.4.1 [bug#261](https://github.com/kazupon/vue-i18n/issues/261))
        // i18n.mergeLocaleMessage(locale, messages)

        messages = Vue.util.extend(i18n.messages[locale] || {}, messages)

        i18n.setLocaleMessage(locale, messages)
      }
    },

    actions: {
      // set current working element id with checking if we need to save on the server current before ...
      setElementId (context, elementId) {
        // do we need to silently save current element (before changing to the requested one) ?
        /* if (!context.getters.userIsGuest) {
          const currentProject = context.getters.project.prepareSend
          const currentElement = context.getters.element

          if (!currentElement.savedOnServer) {
            // first, mark it as saved
            context.dispatch('db/slabs/update', {
              where: currentElement.id,
              data: {
                savedOnServer: true
              }
            })

            // save it on server
            uikit.util.ajax('api/slab', {
              method: 'post',
              data: JSON.stringify({
                slab: currentElement,
                project: currentProject
              }),
              responseType: 'json',
              headers: {
                'Content-Type': 'application/json; charset=UTF-8',
                'X-Requested-With': 'XMLHttpRequest'
              }
            }).then((xhr) => {
              if (xhr.response.success) {
                // update date_last_save (if we have it ...)
                if (xhr.response.slab && xhr.response.slab.date_last_save) {
                  context.commit('allowChanges', true)

                  context.dispatch('db/slabs/update', {
                    where: currentElement.id,
                    data: {
                      date_last_save: xhr.response.slab.date_last_save
                    }
                  }).then(() => {
                    context.commit('allowChanges', false)
                  })
                }

                // do we have to update project date_last_save too ?
                if (xhr.response.project && xhr.response.project.date_last_save) {
                  context.dispatch('db/projects/update', {
                    where: currentProject.id,
                    data: {
                      savedOnServer: true,
                      date_last_save: xhr.response.project.date_last_save
                    }
                  })
                }
              } else {
                // couldn't save, mark it back as unsaved
                context.dispatch('db/slabs/update', {
                  where: currentElement.id,
                  data: {
                    savedOnServer: false
                  }
                })
              }
            }, () => {
              // couldn't save, mark it back as unsaved
              context.dispatch('db/slabs/update', {
                where: currentElement.id,
                data: {
                  savedOnServer: false
                }
              })
            })
          } else if (!currentProject.savedOnServer) {
            // if only project needs save
            uikit.util.ajax('api/project', {
              method: 'post',
              data: JSON.stringify({
                project: currentProject
              }),
              responseType: 'json',
              headers: {
                'Content-Type': 'application/json; charset=UTF-8',
                'X-Requested-With': 'XMLHttpRequest'
              }
            }).then((xhr) => {
              if (xhr.response.success) {
                if (xhr.response.project && xhr.response.project.date_last_save) {
                  context.dispatch('db/projects/update', {
                    where: currentProject.id,
                    data: {
                      savedOnServer: true,
                      date_last_save: xhr.response.project.date_last_save
                    }
                  })
                }
              }
            })
          }
        } */

        // save all unsaved data
        context.dispatch('sync')

        // now we can update the element ...
        context.commit('setElementId', elementId)
      },

      // save ALL unsaved projects & slabs
      sync (context) {
        // only if NOT guest
        if (!context.getters.userIsGuest && !context.getters.isEverythingSaved) {
          const data = context.getters.unsavedData

          const apiPath = data.projects.length ? 'project' : 'slab'

          const dataTypes = ['projects', 'slabs']

          // navigator.sendBeacon polyfill (IE not Edge and iOS Safari not supporting it)
          // beacon should be ok as it doesn't wait for the response, but we've activated user input abort on server so maybe we'll be fine though ...
          // UPDATE: we'll not use sendBeacon as we cannot establish custom headers (content-type worked using Blob type but it seems that not anymore, and X-Requested-With is impossible to set)
          // We also will call this on visibilitychange instead of on unload as proposed here https://w3c.github.io/beacon/#h-note, especially for mobile when the browser will go into background and then closed by OS, thus we will need the response callback to mark those records

          /* if (!navigator.sendBeacon) {
            navigator.sendBeacon = (url, data) =>
              window.fetch(`api/${apiPath}`, {
                method: 'POST',
                body: data,
                credentials: 'include', // send session cookies as by default are NOT
                headers: {
                  'Content-Type': 'application/json; charset=UTF-8',
                  'X-Requested-With': 'XMLHttpRequest'
                }
              })
          } */

          // is this necessary ?
          // context.commit('allowChanges', true)

          // first mark all unsaved projects & elements as saved (and we'll mark them back in case of failure)
          // this is needed if user triggers this often quickly (browse through elements) and the response from server is delayed ...
          // UPDATE: it seems that's not a good idea in case of a server failure (not saved) and window close / refresh, puting them back to not saved might be triggered too late
          /* dataTypes.forEach(type => {
            if (data[type] && data[type].length) {
              data[type].forEach(record => {
                context.dispatch(`db/${type}/update`, {
                  where: record.id,
                  data: {
                    savedOnServer: true
                  }
                })
              })
            }
          }) */

          // we can use the modern Fetch() API, but it's not so much different than old AJAX
          const result = uikit.util.ajax(`api/${apiPath}`, {
            method: 'post',
            data: JSON.stringify({
              customMethod: 'sync',
              ...data
            }),
            responseType: 'json',
            headers: {
              'Content-Type': 'application/json; charset=UTF-8',
              'X-Requested-With': 'XMLHttpRequest'
            }
          })

          result.then(xhr => {
            // update data if still user logged on
            if (!context.getters.userIsGuest) {
              if (xhr.response.success) {
                dataTypes.forEach(type => {
                  // let's parse all sent elements, and check if they're back successfully
                  data[type].forEach(record => {
                    /* const update = {}

                    // if the record came back correctly and contains saved_date
                    if (xhr.response[type] && xhr.response[type][record.id] && xhr.response[type][record.id].date_last_save) {
                      update.date_last_save = xhr.response[type][record.id].date_last_save
                    } else {
                      // otherwyse, let's mark it back as unsaved
                      update.savedOnServer = false
                    }

                    context.dispatch(`db/${type}/update`, {
                      where: record.id,
                      data: update
                    }) */

                    if (xhr.response[type] && xhr.response[type][record.id] && xhr.response[type][record.id].date_last_save) {
                      context.dispatch(`db/${type}/update`, {
                        where: record.id,
                        data: {
                          date_last_save: xhr.response[type][record.id].date_last_save,
                          savedOnServer: true
                        }
                      })
                    }
                  })
                })

                /* if (event.type === 'beforeunload') {
                  uikit.notification(i18n.t('can_close_window'), {pos: 'bottom-right', status: 'success'})
                } */
              } else {
                // insuccess, let's mark them all back as unsaved
                // UPDATE: it seems that's not a good idea in case of a server failure (not saved) and window close / refresh, puting them back to not saved might be triggered too late
                /* dataTypes.forEach(type => {
                  if (data[type] && data[type].length) {
                    data[type].forEach(record => {
                      context.dispatch(`db/${type}/update`, {
                        where: record.id,
                        data: {
                          savedOnServer: false
                        }
                      })
                    })
                  }
                }) */
              }
            }
          }/*, () => {
            // error on server, let's mark them all back as unsaved
            // UPDATE: it seems that's not a good idea in case of a server failure (not saved) and window close / refresh, puting them back to not saved might be triggered too late
            dataTypes.forEach(type => {
              if (data[type] && data[type].length) {
                data[type].forEach(record => {
                  context.dispatch(`db/${type}/update`, {
                    where: record.id,
                    data: {
                      savedOnServer: false
                    }
                  })
                })
              }
            })
          } */)

          // is this necessary ?
          // context.commit('allowChanges', false)

          return result
        }
      },

      // adds or removes Admin main menu
      // default enforces check on the server too, but we can sckip it
      // https://vuejs.org/v2/api/#Vue-set & #Vue-delete in order to set or delete a reactive object property
      menuAdmin (context, options) {
        // default check: local state
        let isAdmin = context.state.user && context.state.user.is_admin && context.state.user.is_admin !== '0'

        // we might want to skip server check as well
        if (!options || !options.skipServerCheck) {
          isAdmin = context.getters.userIsAdmin
        }

        if (isAdmin && options && options.show) {
          Vue.set(context.state.menu, 'reports', 'Reports')
          // Vue.set(context.state.menu, 'admin', 'Admin')
        } else {
          Vue.delete(context.state.menu, 'admin')
          Vue.delete(context.state.menu, 'reports')
        }
      },

      userLogin (context, user) {
        Vue.set(context.state, 'user', user)

        // context.dispatch('menuAdmin', {show: true})
      },

      userLogout (context) {
        // sync unsaved data to server
        // TODO: might be an ideea to do this in the same logout call
        context.dispatch('sync')

        // AJAX to server
        uikit.util.ajax('api/user', {
          method: 'put',
          data: JSON.stringify({
            customMethod: 'logout'
          }),
          responseType: 'json',
          headers: {
            'Content-Type': 'application/json; charset=UTF-8',
            'X-Requested-With': 'XMLHttpRequest'
          }
        }).then((xhr) => {
          if (xhr.response.success) {
            Vue.set(context.state, 'user', null)

            context.dispatch('menuAdmin', {show: false, skipServerCheck: true})

            // check if thre is a localStorage backup (from guest before logon)
            const backup = window.localStorage.getItem(`${context.state.lsKey}_backup`)

            let {projects, selected} = backup ? JSON.parse(backup) : createNewProject(context.getters)

            // inject the elements OVER the existing ones dispatching create
            // allow changes (in order not to yield if locked element with loads or openings)
            context.commit('allowChanges', true)

            context.dispatch('db/projects/create', {
              data: projects
            }).then(() => {
              context.commit('allowChanges', false)
            })

            context.commit('setElementId', selected)

            // delete backup
            if (backup) {
              window.localStorage.removeItem(`${context.state.lsKey}_backup`)
            }
          }
        })
      },

      changeLocale (context, { page, locale }) {
        const setLocale = locale => {
          i18n.locale = locale

          window.localStorage.language = locale
          document.querySelector('html').setAttribute('lang', locale)

          return locale
        }

        // if locale already set
        if (locale === i18n.locale) {
          return Promise.resolve(setLocale(locale))
        }

        // if we already have loaded
        if (context.state.pageLocales[page] && context.state.pageLocales[page][locale]) {
          context.commit('setPageLocaleMessages', { page, locale })

          return Promise.resolve(setLocale(locale))
        }

        // if the page language hasn't been loaded yet
        return import(/* webpackChunkName: "locale-[request]" */ `@/locale/_pages/${page}/${locale}.json`)
          .then(messages => {
            context.commit('setPageLocaleMessages', { page, locale, messages })

            return setLocale(locale)
          })
      }
    },

    plugins: [
      VuexORM.install(database, {
        namespace: 'db'
      })
    ]
  })
}



// WEBPACK FOOTER //
// ./src/store/index.js